import { BaseFeature } from '../../core/base-feature.js';

/**
 * CollarComplianceFeature - Frontend feature for collar compliance verification
 * Handles: enable/disable check mode, request checks, get status/history, configure
 */
export class CollarComplianceFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // State
        this.enabled = false;
        this.score = 50;
        this.level = 'suspicious';
        this.config = {};
        this.recentChecks = [];

        // Event callbacks for React components
        this.complianceUpdateCallbacks = new Set();
        this.checkResultCallbacks = new Set();
        this.updateCallbacks = new Set();

        // Pending promises for request/response
        this._pendingRequests = new Map();
    }

    static getMetadata() {
        return {
            id: 'collar-compliance',
            name: 'Collar Compliance',
            version: 'v1.0',
            capabilities: [
                'enable',
                'disable',
                'request_check',
                'configure',
                'get_status'
            ],
            dependencies: ['pishock']
        };
    }

    async initialize() {
        console.log('[CollarCompliance] Initializing...');
        console.log('[CollarCompliance] Initialized');
    }

    async handleMessage(message) {
        const { action, cmd, payload } = message;
        const command = cmd || action;

        switch (command) {
            case 'state_update':
                return this.handleStateUpdate(payload);

            case 'initial_state':
                return this.handleInitialState(payload);

            // Responses to commands
            case 'enable_response':
                return this.handleResponse('enable', payload);
            case 'disable_response':
                return this.handleResponse('disable', payload);
            case 'request_check_response':
                return this.handleResponse('request_check', payload);
            case 'get_status_response':
                return this.handleResponse('get_status', payload);
            case 'get_history_response':
                return this.handleResponse('get_history', payload);
            case 'configure_response':
                return this.handleResponse('configure', payload);
            case 'prompt_wearing_check_response':
                return this.handleResponse('prompt_wearing_check', payload);
            case 'demand_wearing_response':
                return this.handleResponse('demand_wearing', payload);

            default:
                console.warn(`[CollarCompliance] Unknown command: ${command}`);
        }
    }

    // =====================================
    // Event Handlers
    // =====================================

    handleStateUpdate(payload) {
        if (payload.enabled !== undefined) this.enabled = payload.enabled;
        if (payload.score !== undefined) this.score = payload.score;
        if (payload.level) this.level = payload.level;
        if (payload.config) this.config = payload.config;
        if (payload.recentChecks) this.recentChecks = payload.recentChecks;

        this.notifyComplianceUpdateListeners(payload);
        this.notifyUpdateListeners();
    }

    handleInitialState(payload) {
        console.log('[CollarCompliance] Received initial state');
        if (payload.enabled !== undefined) this.enabled = payload.enabled;
        if (payload.score !== undefined) this.score = payload.score;
        if (payload.level) this.level = payload.level;
        if (payload.config) this.config = payload.config;
        if (payload.recentChecks) this.recentChecks = payload.recentChecks;
        this.notifyUpdateListeners();
    }

    handleResponse(requestType, payload) {
        const resolve = this._pendingRequests.get(requestType);
        if (resolve) {
            this._pendingRequests.delete(requestType);
            resolve(payload);
        }
    }

    // =====================================
    // Command Methods
    // =====================================

    async enableCheckMode(config = {}) {
        return new Promise((resolve) => {
            this.sendMessage('enable', config);
            this._pendingRequests.set('enable', resolve);
        });
    }

    async disableCheckMode() {
        return new Promise((resolve) => {
            this.sendMessage('disable', {});
            this._pendingRequests.set('disable', resolve);
        });
    }

    async requestCheck(strategy = 'random') {
        return new Promise((resolve) => {
            this.sendMessage('request_check', { strategy });
            this._pendingRequests.set('request_check', resolve);
        });
    }

    async getComplianceStatus() {
        return new Promise((resolve) => {
            this.sendMessage('get_status', {});
            this._pendingRequests.set('get_status', resolve);
        });
    }

    async getCheckHistory(limit = 20) {
        return new Promise((resolve) => {
            this.sendMessage('get_history', { limit });
            this._pendingRequests.set('get_history', resolve);
        });
    }

    async configure(settings) {
        return new Promise((resolve) => {
            this.sendMessage('configure', settings);
            this._pendingRequests.set('configure', resolve);
        });
    }

    async promptWearingCheck() {
        return new Promise((resolve) => {
            this.sendMessage('prompt_wearing_check', {});
            this._pendingRequests.set('prompt_wearing_check', resolve);
        });
    }

    async demandWearing() {
        return new Promise((resolve) => {
            this.sendMessage('demand_wearing', {});
            this._pendingRequests.set('demand_wearing', resolve);
        });
    }

    // =====================================
    // Event Subscriptions (for React)
    // =====================================

    onComplianceUpdate(callback) {
        this.complianceUpdateCallbacks.add(callback);
        return () => this.complianceUpdateCallbacks.delete(callback);
    }

    onCheckResult(callback) {
        this.checkResultCallbacks.add(callback);
        return () => this.checkResultCallbacks.delete(callback);
    }

    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyComplianceUpdateListeners(data) {
        for (const callback of this.complianceUpdateCallbacks) {
            try { callback(data); } catch (error) {
                console.error('[CollarCompliance] Error in compliance update callback:', error);
            }
        }
    }

    notifyCheckResultListeners(data) {
        for (const callback of this.checkResultCallbacks) {
            try { callback(data); } catch (error) {
                console.error('[CollarCompliance] Error in check result callback:', error);
            }
        }
    }

    notifyUpdateListeners() {
        const data = {
            enabled: this.enabled,
            score: this.score,
            level: this.level,
            config: this.config,
            recentChecks: this.recentChecks
        };
        for (const callback of this.updateCallbacks) {
            try { callback(data); } catch (error) {
                console.error('[CollarCompliance] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // Getters
    // =====================================

    isEnabled() { return this.enabled; }
    getScore() { return this.score; }
    getLevel() { return this.level; }
    getConfig() { return this.config; }
    getRecentChecks() { return this.recentChecks; }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[CollarCompliance] Cleaning up...');
        this.complianceUpdateCallbacks.clear();
        this.checkResultCallbacks.clear();
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
